import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "../lib/api";
import { cleanName, initials } from "./PlayerCard";

const LIST_LABEL = { hall_of_fame: "Top 20", historic: "Historic", ones_to_watch: "Ones to Watch" };

let cache = null;

export const PlayerSearch = ({ variant = "block", onClose, testId = "player-search" }) => {
  const [players, setPlayers] = useState(cache || []);
  const [term, setTerm] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (cache) return;
    api.get("/players?limit=200").then((r) => {
      cache = r.data;
      setPlayers(r.data);
    });
  }, []);

  const results = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return [];
    return players
      .filter((p) =>
        [cleanName(p.name), p.club, p.league, p.region].some((f) => (f || "").toLowerCase().includes(t))
      )
      .slice(0, 7);
  }, [term, players]);

  useEffect(() => setActive(0), [term]);

  const go = (player) => {
    if (!player) return;
    setTerm("");
    onClose?.();
    navigate(`/hall-of-fame/${player.slug}`);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results.length) go(results[active]);
      else if (term.trim()) navigate(`/hall-of-fame?q=${encodeURIComponent(term.trim())}`);
      onClose?.();
    } else if (e.key === "Escape") {
      onClose?.();
    }
  };

  const compact = variant === "nav";

  return (
    <div className="relative w-full" ref={boxRef} data-testid={testId}>
      <div
        className={`flex items-center gap-3 border bg-[#0b0b0b] ${
          compact ? "border-transparent px-1 py-1" : "border-white/15 px-5 py-4 focus-within:border-ochre"
        } transition-colors`}
      >
        <Search size={compact ? 18 : 20} className="shrink-0 text-ochre" />
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={onKeyDown}
          autoFocus={compact}
          placeholder="Find a star — name, club, league or region"
          aria-label="Find a player"
          data-testid={`${testId}-input`}
          className={`w-full border-none bg-transparent text-white placeholder:text-white/40 focus:outline-none ${
            compact ? "text-sm" : "font-display text-base uppercase tracking-wide md:text-lg"
          }`}
        />
        {term ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => setTerm("")}
            data-testid={`${testId}-clear`}
            className="shrink-0 text-white/50 hover:text-white"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      <AnimatePresence>
        {term.trim() ? (
          <motion.ul
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="absolute inset-x-0 top-[calc(100%+6px)] z-[120] max-h-[60vh] overflow-y-auto border border-ochre/40 bg-black shadow-[0_20px_60px_rgba(0,0,0,0.9)]"
            data-testid={`${testId}-results`}
          >
            {results.length === 0 ? (
              <li className="px-5 py-4 text-sm text-white/50" data-testid={`${testId}-empty`}>
                No player matches “{term}”.
              </li>
            ) : (
              results.map((p, i) => (
                <li key={p.slug}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(p)}
                    data-testid={`${testId}-result-${p.slug}`}
                    className={`flex w-full items-center gap-4 border-b border-white/10 px-4 py-3 text-left transition-colors last:border-b-0 ${
                      i === active ? "bg-maasai/15" : "hover:bg-white/5"
                    }`}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-ochre/15 font-display text-xs font-bold text-ochre">
                      {initials(cleanName(p.name))}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-display text-base uppercase tracking-tight text-white">
                        {cleanName(p.name)}
                      </span>
                      <span className="block truncate text-[0.66rem] uppercase tracking-[0.16em] text-white/45">
                        {[p.club, p.league].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span className="shrink-0 text-[0.6rem] font-bold uppercase tracking-[0.16em] text-ochre">
                      {p.rank ? `#${p.rank}` : LIST_LABEL[p.list_type]}
                    </span>
                  </button>
                </li>
              ))
            )}
          </motion.ul>
        ) : null}
      </AnimatePresence>
    </div>
  );
};
